import { useQuery } from "@tanstack/react-query";

import { env } from "@/config";
import { usePartnerConfig } from "@/partner/usePartnerConfig";

import type { MetricBucket, PayMetrics } from "./types";

import { EMPTY_HUB_METRICS } from "../emptyHubMetrics";
import { EXPERT_HUB_ACTIONS } from "./expertHubErrorMetric";
import { getPayMetrics } from "./getPayMetrics";
import { getMockPayMetrics } from "./mocks";
import { isDataverseCard } from "./utils";

export interface PayMetricsResult {
    payMetrics: PayMetrics | null;
    isLoading: boolean;
    isError: boolean;
}

function isEmpty(bucket: MetricBucket): boolean {
    return Object.keys(bucket).length === 0;
}

function mergeBucket(real: MetricBucket, mock: MetricBucket): MetricBucket {
    if (isEmpty(real)) return mock;
    const filled = { ...real };
    for (const key of Object.keys(mock)) {
        if (!(key in filled)) {
            filled[key] = mock[key];
        }
    }
    return filled;
}

function mergeWithMocks(data: PayMetrics): PayMetrics {
    const mock = getMockPayMetrics();
    return {
        ...data,
        modifiedOn: data.modifiedOn ?? mock.modifiedOn,
        totals: {
            serve: mergeBucket(data.totals.serve, mock.totals.serve),
            solve: mergeBucket(data.totals.solve, mock.totals.solve),
            sell: mergeBucket(data.totals.sell, mock.totals.sell),
        },
    };
}

export function usePayMetrics(): PayMetricsResult {
    const { hubMetrics } = usePartnerConfig();
    // Pay metrics only feed Dataverse cards, so a partner configured without any never fetches them.
    const hasPayCards = (hubMetrics ?? EMPTY_HUB_METRICS).some(isDataverseCard);

    const { data, isLoading, isError } = useQuery({
        queryKey: ["expertHub", "payMetrics"],
        queryFn: getPayMetrics,
        enabled: hasPayCards,
        staleTime: 30 * 60_000,
        refetchInterval: 30 * 60_000,
        retry: 2,
        // Reject signal → QueryCache.onError (see queryServices); bad-shape + empty → getPayMetrics.
        meta: { expertHubErrorAction: EXPERT_HUB_ACTIONS.payMetricsFetchFailed },
    });

    let payMetrics: PayMetrics | null = data ?? null;
    if (!env.isProdMode) {
        payMetrics = data ? mergeWithMocks(data) : getMockPayMetrics();
    }

    return {
        payMetrics,
        isLoading,
        // Non-prod always has mock data to show, so a failed fetch is not a user-facing error there —
        // reporting it would paint the cards red over usable mock values. Only prod surfaces the error state.
        isError: env.isProdMode ? isError : false,
    };
}
